import { useState, useEffect } from 'react';
import { PersonFormContent } from '@/components/persons/PersonFormContent';
import { usePersonProfiles } from '@/hooks/usePersonProfiles';
import { useFamily } from '@/hooks/useFamily';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/PageHeader';
import { useCurrentSection } from '@/hooks/useCurrentSection';
import { Button } from '@/components/ui/button';

export default function CreatePerson() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { family, loading: familyLoading } = useFamily();
  const { createPerson } = usePersonProfiles(family?.id);
  const { toast } = useToast();
  const { primaryVariant } = useCurrentSection();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ first_name: '' });

  // Get initial category from URL params (e.g., ?category=family)
  const initialCategory = searchParams.get('category') || 'family';

  // Redirect if no family (only after loading finishes)
  useEffect(() => {
    if (!familyLoading && !family) {
      toast({
        title: "Famille requise",
        description: "Vous devez d'abord créer ou rejoindre une famille.",
        variant: "destructive",
      });
      navigate('/dashboard');
    }
  }, [family, familyLoading, navigate, toast]);

  const handleSubmit = async (data: any) => {
    if (!family) return;
    
    setIsLoading(true);
    try {
      await createPerson(data);
      // Give some time for the data to be updated
      setTimeout(() => {
        navigate('/persons');
      }, 500);
    } catch (error) {
      // Error handling is done in the hook
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormDataChange = (data: any) => {
    setFormData(data);
  };

  if (familyLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <PageHeader 
          title="Ajouter une personne" 
          backPath="/persons" 
          section="persons" 
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!family) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader 
        title="Ajouter une personne" 
        backPath="/persons" 
        section="persons" 
      />

      <div className="flex-1 px-4 pt-2 pb-32">
        <PersonFormContent
          onSubmit={handleSubmit}
          initialData={{ category: initialCategory as any }}
          isSubmitting={isLoading}
          showActions={false}
          onFormDataChange={handleFormDataChange}
        />
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 border-t bg-background space-y-3 z-50">
        <Button 
          disabled={isLoading || !formData.first_name?.trim()}
          className="w-full"
          variant={primaryVariant as any}
          form="person-form"
          type="submit"
        >
          {isLoading ? "Enregistrement..." : "Ajouter la personne"}
        </Button>
        <Button 
          variant="cancel-hover" 
          onClick={() => navigate('/persons')}
          className="w-full"
        >
          Annuler
        </Button>
      </div>
    </div>
  );
}